import { useEffect, useState, useCallback } from "react";
import { FileText, Briefcase, Plus, X, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { PageHeader, Scroller, Spinner, EmptyState, goldBtn } from "@/pages/_shared";

const STATUS_COLORS = {
  draft: "text-white/50", sent: "text-blue-300", accepted: "text-emerald-400", rejected: "text-red-400",
  scheduled: "text-gold/80", in_progress: "text-blue-300", completed: "text-emerald-400",
  unpaid: "text-gold/80", paid: "text-emerald-400", overdue: "text-red-400",
};

export function DealPage({ kind }) {
  const { can, activeCompanyId } = useOrg();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_name: "", title: "", items: [{ desc: "", qty: 1, price: 0 }], tax_rate: 20, description: "" });

  const conf = {
    quotes: { title: "Quotes", icon: FileText, editPerm: "quotes.edit", statuses: ["draft", "sent", "accepted", "rejected"], hasItems: true },
    jobs: { title: "Jobs", icon: Briefcase, editPerm: "jobs.edit", statuses: ["scheduled", "in_progress", "completed"], hasItems: false },
    invoices: { title: "Invoices", icon: Receipt, editPerm: "invoices.edit", statuses: ["unpaid", "paid", "overdue"], hasItems: true },
  }[kind];

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/${kind}`); setRows(data); } catch (e) { setRows([]); }
  }, [kind]);
  useEffect(() => { setRows(null); load(); }, [kind, load, activeCompanyId]);

  if (rows === null) return <Spinner />;

  const setItem = (i, k, v) => { const items = [...form.items]; items[i] = { ...items[i], [k]: v }; setForm({ ...form, items }); };
  const create = async () => {
    try {
      if (kind === "jobs") await api.post("/jobs", { title: form.title, customer_name: form.customer_name, description: form.description });
      else await api.post(`/${kind}`, { title: form.title || conf.title, customer_name: form.customer_name, items: form.items, tax_rate: Number(form.tax_rate) });
      setOpen(false); setForm({ customer_name: "", title: "", items: [{ desc: "", qty: 1, price: 0 }], tax_rate: 20, description: "" });
      toast.success("Created"); load();
    } catch (e) { toast.error("Could not create"); }
  };
  const setStatus = async (id, status) => { await api.patch(`/${kind}/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); };

  const Icon = conf.icon;
  return (
    <Scroller>
      <PageHeader title={conf.title} subtitle={`Manage ${conf.title.toLowerCase()}`} icon={Icon}
        action={can(conf.editPerm) && <button onClick={() => setOpen(true)} className={goldBtn} data-testid={`${kind}-add`}><Plus className="h-4 w-4" /> New</button>} />

      {rows.length === 0 ? (
        <EmptyState icon={Icon} title={`No ${conf.title.toLowerCase()} yet`} />
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between rounded-xl border border-white/8 bg-charcoal px-4 py-3" data-testid={`${kind}-row`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-white/85">{r.number ? `${r.number} · ` : ""}{r.title || r.customer_name || "—"}</p>
                <p className="text-xs text-white/40">{r.customer_name || ""}{r.total != null ? ` · £${r.total}` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs capitalize ${STATUS_COLORS[r.status] || "text-white/50"}`}>{(r.status || "").replace("_", " ")}</span>
                {can(conf.editPerm) && (
                  <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} data-testid={`${kind}-status-${r.id}`}
                    className="rounded-lg border border-white/10 bg-graphite px-2 py-1 text-xs text-white/70 focus:outline-none">
                    {conf.statuses.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-charcoal p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-lg font-semibold">New {conf.title.slice(0, -1)}</h2><button onClick={() => setOpen(false)}><X className="h-5 w-5 text-white/40" /></button></div>
            <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Customer name" data-testid={`${kind}-customer`}
              className="mb-3 w-full rounded-xl border border-white/10 bg-graphite px-4 py-2.5 text-sm focus:border-gold/40 focus:outline-none" />
            {kind !== "invoices" && (
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" data-testid={`${kind}-title`}
                className="mb-3 w-full rounded-xl border border-white/10 bg-graphite px-4 py-2.5 text-sm focus:border-gold/40 focus:outline-none" />
            )}
            {kind === "jobs" && (
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Description"
                className="mb-3 w-full resize-none rounded-xl border border-white/10 bg-graphite px-4 py-2.5 text-sm focus:border-gold/40 focus:outline-none" />
            )}
            {conf.hasItems && (
              <div className="mb-3 space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="flex gap-2">
                    <input value={it.desc} onChange={(e) => setItem(i, "desc", e.target.value)} placeholder="Item" className="flex-1 rounded-lg border border-white/10 bg-graphite px-3 py-2 text-sm focus:outline-none" />
                    <input type="number" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} placeholder="Qty" className="w-16 rounded-lg border border-white/10 bg-graphite px-2 py-2 text-sm focus:outline-none" />
                    <input type="number" value={it.price} onChange={(e) => setItem(i, "price", e.target.value)} placeholder="£" className="w-20 rounded-lg border border-white/10 bg-graphite px-2 py-2 text-sm focus:outline-none" />
                  </div>
                ))}
                <button onClick={() => setForm({ ...form, items: [...form.items, { desc: "", qty: 1, price: 0 }] })} className="text-xs text-gold/70 hover:text-gold">+ Add line</button>
              </div>
            )}
            <button onClick={create} className={`${goldBtn} mt-2 w-full justify-center`} data-testid={`${kind}-save`}>Create {conf.title.slice(0, -1)}</button>
          </div>
        </div>
      )}
    </Scroller>
  );
}
